// Broadcast Studio — a compact newsroom surface for internal updates and
// external release notes. The page supplies data/state; cards and the composer
// stay deliberately editorial rather than imitating a generic blog grid.

export const BroadcastAudienceSegment = {
  tag: 'button',
  type: 'button',
  flex: '1 1 0',
  minHeight: 'controlHCompact',
  padding: 'Y Z1',
  round: 'radiusPill',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z1',
  fontWeight: (el, s) => ((s.root?._broadcastScope || 'internal') === el.value ? '700' : '500'),
  background: (el, s) =>
    (s.root?._broadcastScope || 'internal') === el.value ? 'selected' : 'transparent',
  color: (el, s) => ((s.root?._broadcastScope || 'internal') === el.value ? 'title' : 'caption'),
  ariaPressed: (el, s) => ((s.root?._broadcastScope || 'internal') === el.value ? 'true' : 'false'),
  ':hover': { background: 'hover', color: 'title' },
  text: (el) => el.call('polyglot', el.label),
  onClick: (event, el) => {
    event.preventDefault()
    el.call('setBroadcastScope', el.value)
  }
}

export const BroadcastTagFilter = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  width: '100%',
  padding: 'Z A',
  round: 'radiusCard',
  border: 'none',
  background: (el, s) => ((s.root?._broadcastTag || 'all') === s.key ? 'selected' : 'transparent'),
  color: (el, s) => ((s.root?._broadcastTag || 'all') === s.key ? 'title' : 'caption'),
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 'Z1',
  fontWeight: (el, s) => ((s.root?._broadcastTag || 'all') === s.key ? '700' : '500'),
  ':hover': { background: 'hover', color: 'title' },
  onClick: (event, el, s) => {
    event.preventDefault()
    el.call('setBroadcastTag', s.key)
  },
  // KEY-IN-STATE for the built-in "all" row; a real TOPIC name is a
  // non-key and polyglot returns it verbatim.
  Label: { tag: 'span', text: (el, s) => el.call('polyglot', s.label || '') },
  Count: { tag: 'span', fontFamily: 'Code', fontSize: 'Y2', text: (el, s) => String(s.count || 0) }
}

export const BroadcastStoryCard = {
  tag: 'article',
  flow: 'y',
  gap: 'B',
  padding: 'C',
  round: 'radiusSheet',
  background: (el, s) => (s.audience === 'external' ? 'purple+30' : 'green+30'),
  color: 'black',
  position: 'relative',
  cursor: 'pointer',
  transition: '120ms defaultBezier transform, 120ms defaultBezier background',
  role: 'button',
  tabindex: '0',
  ':hover': { transform: 'translateY(-W)' },
  onRender: (el) => el.call('armLongPress'),
  onContextmenu: (event, el, s) =>
    el.call('openAppContextMenu', event, { kind: 'broadcast', id: s.id }),
  onClick: (event, el, s) => {
    const target = event.target
    if (target && target.closest && target.closest('button, a')) return
    el.call('openBroadcastDetail', s)
  },
  onKeydown: (event, el, s, context) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    const target = event.target
    if (target && target.closest && target.closest('button, a')) return
    event.preventDefault()
    el.onClick(event, el, s, context)
  },

  CardHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'A',
    Audience: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      text: (el, s) =>
        el.call(
          'polyglot',
          s.audience === 'external'
            ? 'workspace.broadcastStudio.preview.audienceExternal'
            : 'workspace.broadcastStudio.preview.audienceWorkspace'
        )
    },
    Tools: {
      flow: 'x',
      align: 'center',
      gap: 'V',
      Edit: {
        tag: 'button',
        type: 'button',
        hide: (el, s) => !el.call('canEditBroadcast', s, s.root),
        padding: 'V Z',
        minHeight: 'controlHCompact',
        round: 'radiusPill',
        border: 'none',
        color: 'black',
        background: 'black.1',
        fontSize: 'Y2',
        fontWeight: '700',
        cursor: 'pointer',
        text: '{{ workspace.broadcastStudio.card.edit | polyglot }}',
        onClick: (event, el, s) => {
          event.stopPropagation()
          el.call('openBroadcastComposer', s)
        }
      },
      Delete: {
        tag: 'button',
        type: 'button',
        hide: (el, s) => !el.call('canEditBroadcast', s, s.root),
        minWidth: 'controlHCompact',
        minHeight: 'controlHCompact',
        padding: 'V Z',
        round: '50%',
        border: 'none',
        color: 'black',
        background: 'transparent',
        cursor: 'pointer',
        ariaLabel: (el) => el.call('polyglot', 'workspace.broadcastStudio.card.deleteAria'),
        fontSize: 'A',
        fontWeight: '700',
        text: '×',
        onClick: (event, el, s) => {
          event.stopPropagation()
          el.call('confirmDeleteBroadcast', s)
        }
      },
      More: {
        extends: 'AppContextMenuButton',
        ariaLabel: (el) => el.call('polyglot', 'workspace.broadcastStudio.card.moreAria'),
        onClick: (event, el, s) =>
          el.call('openAppContextMenu', event, { kind: 'broadcast', id: s.id }, { anchor: true })
      }
    }
  },

  Copy: {
    flow: 'y',
    gap: 'Z',
    Tag: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '700',
      opacity: '.55',
      text: (el, s) => s.tag || el.call('polyglot', 'workspace.broadcastStudio.preview.tagDefault')
    },
    Title: {
      tag: 'h2',
      margin: '0',
      fontSize: 'D',
      fontWeight: '700',
      lineHeight: '1.08',
      letterSpacing: '-0.02em',
      text: (el, s) => s.title || el.call('polyglot', 'workspace.broadcastStudio.preview.untitled')
    },
    Excerpt: {
      tag: 'p',
      margin: '0',
      maxWidth: 'H',
      fontSize: 'A',
      lineHeight: '1.45',
      opacity: '.7',
      text: (el, s) => s.excerpt || (s.body || [])[0] || ''
    }
  },

  ExternalLink: {
    extends: 'Link',
    hide: (el, s) => s.audience !== 'external' || !s.externalUrl,
    href: (el, s) => s.externalUrl,
    target: '_blank',
    rel: 'noreferrer',
    alignSelf: 'flex-start',
    padding: 'V Z',
    round: 'radiusPill',
    background: 'black',
    color: 'white',
    fontSize: 'Y2',
    fontWeight: '700',
    textDecoration: 'none',
    text: '{{ workspace.broadcastStudio.card.openExternal | polyglot }}',
    onClick: (event) => event.stopPropagation()
  },

  CardFoot: {
    flow: 'x',
    align: 'center space-between',
    gap: 'A',
    flexWrap: 'wrap',
    Byline: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      Avatar: {
        boxSize: 'A1',
        round: '50%',
        src: (el, s) => s.authorAvatar || el.call('avatarUrlFor', s.author)
      },
      Name: { tag: 'span', fontSize: 'Z1', fontWeight: '700', text: (el, s) => s.author || '' },
      Meta: {
        tag: 'span',
        fontFamily: 'Code',
        fontSize: 'Y2',
        opacity: '.55',
        text: (el, s) =>
          [el.call('formatShortDate', s.date), el.call('broadcastReadTime', s)]
            .filter(Boolean)
            .join(' · ')
      }
    },
    Reactions: {
      flow: 'x',
      align: 'center',
      gap: 'V',
      flexWrap: 'wrap',
      Existing: {
        flow: 'x',
        align: 'center',
        gap: 'V',
        flexWrap: 'wrap',
        childExtends: 'ReactionPill',
        childrenAs: 'state',
        children: (el, s) =>
          Object.entries(s.reactions || {}).map(([emoji, names]) => ({
            emoji,
            names,
            postId: s.id
          }))
      },
      Add: { extends: 'AddReactionPicker' }
    }
  }
}

export const BroadcastComposerModal = {
  tag: 'form',
  flow: 'y',
  gap: 'B',
  // The modal WINDOW owns the inset at every width. This body only keeps the
  // room its fields' focus ring (3 px out) needs inside its own scroll box.
  // It was 'C' on top of the window's padding: at 390 the field column was
  // 215 px of a 351 px window and the Summary placeholder was cut (dev
  // 99aaab353, 2026-10-02).
  padding: 'X',
  width: '100%',
  maxHeight: '85vh',
  overflow: 'auto',
  // Publish asks first (functions/broadcast.js submitBroadcastDraft); the
  // callback runs only once the post is stored.
  onSubmit: (event, el) => {
    event.preventDefault()
    el.call('submitBroadcastDraft', () => el.call('markPageSaved', el))
  },

  Header: {
    flow: 'x',
    align: 'flex-start space-between',
    gap: 'B',
    Intro: {
      flow: 'y',
      gap: 'V',
      Title: {
        tag: 'h2',
        margin: '0',
        fontSize: 'D',
        fontWeight: '700',
        color: 'title',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?._broadcastDraft?.id
              ? 'workspace.broadcastStudio.composer.titleEdit'
              : 'workspace.broadcastStudio.composer.titleNew'
          )
      },
      Caption: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z1',
        color: 'caption',
        text: '{{ workspace.broadcastStudio.composer.caption | polyglot }}'
      }
    },
    // THE house close X (brand CloseButton — F2, one X for every panel).
    Close: {
      extends: 'CloseButton',
      ariaLabel: (el) => el.call('polyglot', 'workspace.broadcastStudio.composer.closeAria'),
      onClick: (event, el) => {
        event.preventDefault()
        el.call('closeModal', 'broadcast-compose')
      }
    }
  },

  Audience: {
    flow: 'x',
    gap: 'Y',
    ariaLabel: (el) => el.call('polyglot', 'workspace.broadcastStudio.composer.audienceAria'),
    Internal: {
      extends: 'BroadcastComposerAudienceButton',
      value: 'internal',
      label: 'workspace.broadcastStudio.audience.workspace'
    },
    External: {
      extends: 'BroadcastComposerAudienceButton',
      value: 'external',
      label: 'workspace.broadcastStudio.audience.external'
    }
  },

  // Keep this directly under the audience choice. A destination is the
  // defining extra piece of an external release, so it should not be hidden
  // beneath a long update field.
  //
  // Every composer field is the shared field kit (shared/components/
  // AppField.js): AppField owns the caption label, AppInput / AppTextarea
  // the control surface; the fields keep the behaviour only. The wrapper
  // stays a <label> so a click on the caption still focuses the control.
  // `label` is a FUNCTION prop — the kit also names the control with it,
  // and an attribute never resolves a `{{ }}` template.
  ExternalDestination: {
    extends: 'AppField',
    tag: 'label',
    hide: (el, s) => s.root?._broadcastDraft?.audience !== 'external',
    label: (el) => el.call('polyglot', 'workspace.broadcastStudio.field.destination'),
    AppInput: {
      type: 'url',
      name: 'external-url',
      inputmode: 'url',
      autocomplete: 'url',
      placeholder: 'https://…',
      value: (el, s) => s.root?._broadcastDraft?.externalUrl || '',
      onInput: (event, el) => el.call('setBroadcastDraft', 'externalUrl', event.target.value)
    }
  },

  Fields: {
    flow: 'y',
    gap: 'A',
    // `Headline` names a registered component (brand + default-config); the
    // explicit `extends` switches the key's auto-extend off, so the field is
    // the kit's AppField only.
    Headline: {
      extends: 'AppField',
      tag: 'label',
      label: (el) => el.call('polyglot', 'workspace.broadcastStudio.field.headline'),
      AppInput: {
        type: 'text',
        name: 'headline',
        autocomplete: 'off',
        required: true,
        placeholder: (el) =>
          el.call('polyglot', 'workspace.broadcastStudio.field.headlinePlaceholder'),
        value: (el, s) => s.root?._broadcastDraft?.title || '',
        onInput: (event, el) => el.call('setBroadcastDraft', 'title', event.target.value)
      }
    },
    Summary: {
      extends: 'AppField',
      tag: 'label',
      label: (el) => el.call('polyglot', 'workspace.broadcastStudio.field.summary'),
      AppInput: {
        type: 'text',
        name: 'summary',
        autocomplete: 'off',
        placeholder: (el) =>
          el.call('polyglot', 'workspace.broadcastStudio.field.summaryPlaceholder'),
        value: (el, s) => s.root?._broadcastDraft?.excerpt || '',
        onInput: (event, el) => el.call('setBroadcastDraft', 'excerpt', event.target.value)
      }
    },
    Body: {
      extends: 'AppField',
      tag: 'label',
      label: (el) => el.call('polyglot', 'workspace.broadcastStudio.field.update'),
      AppTextarea: {
        name: 'body',
        required: true,
        // The update is the long part of a release — the box opens tall
        // instead of at the kit's one-line control height.
        minHeight: 'F',
        placeholder: (el) =>
          el.call('polyglot', 'workspace.broadcastStudio.field.updatePlaceholder'),
        value: (el, s) => s.root?._broadcastDraft?.body || '',
        onInput: (event, el) => el.call('setBroadcastDraft', 'body', event.target.value)
      }
    },
    Details: {
      tag: 'div',
      flow: 'x',
      gap: 'A',
      '@mobileL': { flow: 'y' },
      Topic: {
        extends: 'AppField',
        tag: 'label',
        flex: '1',
        label: (el) => el.call('polyglot', 'workspace.broadcastStudio.field.topic'),
        AppInput: {
          type: 'text',
          name: 'topic',
          autocomplete: 'off',
          placeholder: (el) =>
            el.call('polyglot', 'workspace.broadcastStudio.field.topicPlaceholder'),
          value: (el, s) => s.root?._broadcastDraft?.tag || '',
          onInput: (event, el) => el.call('setBroadcastDraft', 'tag', event.target.value)
        }
      }
    }
  },

  Error: {
    hide: (el, s) => !s.root?._broadcastSaveError,
    tag: 'p',
    margin: '0',
    padding: 'Z A',
    round: 'radiusControl',
    background: 'red.15',
    color: 'dangerInk',
    fontSize: 'Z1',
    role: 'status',
    ariaLive: 'polite',
    text: (el, s) => s.root?._broadcastSaveError || ''
  },

  Footer: {
    flow: 'x',
    align: 'center space-between',
    gap: 'A',
    flexWrap: 'wrap',
    Hint: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root?._broadcastDraft?.audience === 'external'
            ? 'workspace.broadcastStudio.composer.externalHint'
            : 'workspace.broadcastStudio.composer.workspaceHint'
        )
    },
    Publish: {
      tag: 'button',
      type: 'submit',
      minHeight: 'controlH',
      padding: '0 A',
      round: 'radiusPill',
      border: 'none',
      background: 'primary',
      color: 'primary-content',
      fontSize: 'Z1',
      fontWeight: '700',
      cursor: (el, s) => (s.root?._broadcastSaving ? 'not-allowed' : 'pointer'),
      disabled: (el, s) => !!s.root?._broadcastSaving,
      ariaBusy: (el, s) => (s.root?._broadcastSaving ? 'true' : 'false'),
      ':disabled': { opacity: '.5' },
      text: (el, s) =>
        el.call(
          'polyglot',
          s.root?._broadcastSaving
            ? 'workspace.broadcastStudio.composer.publishing'
            : s.root?._broadcastDraft?.id
              ? 'workspace.broadcastStudio.composer.saveChanges'
              : 'workspace.broadcastStudio.composer.publish'
        )
    }
  }
}

export const BroadcastComposerAudienceButton = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center',
  gap: 'Z',
  flex: '1 1 0',
  minHeight: 'controlH',
  padding: '0 A',
  round: 'radiusControl',
  border: 'none',
  background: (el, s) => (s.root?._broadcastDraft?.audience === el.value ? 'selected' : 'overlay'),
  color: (el, s) => (s.root?._broadcastDraft?.audience === el.value ? 'title' : 'caption'),
  fontSize: 'Z1',
  fontWeight: (el, s) => (s.root?._broadcastDraft?.audience === el.value ? '700' : '500'),
  cursor: 'pointer',
  ariaPressed: (el, s) => (s.root?._broadcastDraft?.audience === el.value ? 'true' : 'false'),
  ':hover': { background: 'hover', color: 'title' },
  Label: { tag: 'span', text: (el) => el.call('polyglot', el.parent.label) },
  onClick: (event, el) => {
    event.preventDefault()
    el.call('setBroadcastDraft', 'audience', el.value)
  }
}
